/**
 * keywordSuggestion.ts
 *
 * Lightweight helper that produces a short, contextual suggestion snippet for a
 * focus-term keyword. Used by the inline hover tooltip in TipTapBlogEditor: when
 * a user hovers a `.focus-term-highlight` span (carrying `data-focus-term`), we
 * read the term and ask this helper for something useful to show.
 *
 * There is intentionally NO backend call here. We derive the snippet from data
 * that is already available client-side (optional keyword metrics + the term's
 * occurrence count in the current document).
 *
 * TODO(suggestions): wire to a real suggestion-pipeline endpoint when available
 * (e.g. an LLM-generated sentence that naturally uses the keyword). Until then
 * this returns a deterministic, metric-aware tip with zero network cost and no
 * Google Ads APIs involved.
 */

/** Optional per-keyword metrics. All fields optional so partial data still helps. */
export interface KeywordMetric {
  /** The keyword phrase (case-insensitive match against the hovered term). */
  keyword: string;
  search_volume?: number | null;
  keyword_difficulty?: number | null;
  cpc?: number | null;
}

export interface KeywordSuggestionInput {
  /** The hovered focus term (already lowercased by the decoration). */
  term: string;
  /** How many times the term appears in the current document. */
  occurrences?: number;
  /** Optional metrics for all target keywords (any source EXCEPT Google Ads). */
  metrics?: KeywordMetric[];
  /** Total word count of the document — enables a keyword-density estimate. */
  totalWords?: number;
}

/** Title-case a term for friendlier display ("dog fence" -> "Dog Fence"). */
function toDisplayTerm(term: string): string {
  return term
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatVolume(v: number): string {
  if (v >= 1000) {
    const k = v / 1000;
    return `${Number.isInteger(k) ? k : k.toFixed(1)}k`;
  }
  return String(v);
}

/** Pick a usage tip based on how often the term currently appears. */
function densityTip(occurrences: number): string {
  if (occurrences <= 0) return 'This term is not yet used — work it into a paragraph naturally.';
  if (occurrences === 1) return 'Used once. Consider adding it to a subheading (H2/H3) for stronger relevance.';
  if (occurrences <= 3) return 'Good coverage. Keep usage natural and avoid forcing extra mentions.';
  return 'Used frequently — watch for keyword stuffing; vary phrasing where you can.';
}

/**
 * Build a short suggestion snippet for a hovered focus term.
 * Returns a single human-readable string suitable for a tooltip body.
 */
export function getKeywordSuggestion(input: KeywordSuggestionInput): string {
  const { term, occurrences = 0, metrics, totalWords } = input;
  const display = toDisplayTerm(term);

  // Locate metrics for this term (case-insensitive).
  const metric = metrics?.find(
    (m) => m.keyword?.trim().toLowerCase() === term.trim().toLowerCase()
  );

  const parts: string[] = [];

  // Occurrence count (always available, derived from the document).
  parts.push(`used ${occurrences} ${occurrences === 1 ? 'time' : 'times'}`);

  // Keyword-density estimate when we know the document word count. The term may
  // be multi-word, so weight occurrences by the number of words it contains.
  if (totalWords && totalWords > 0) {
    const termWordCount = term.trim().split(/\s+/).filter(Boolean).length || 1;
    const density = ((occurrences * termWordCount) / totalWords) * 100;
    parts.push(`density ${density.toFixed(1)}%`);
  }

  // Metric fragments, included only when present.
  if (metric) {
    if (metric.search_volume != null && metric.search_volume >= 0) {
      parts.push(`SV ${formatVolume(metric.search_volume)}`);
    }
    if (metric.keyword_difficulty != null && metric.keyword_difficulty >= 0) {
      parts.push(`KD ${metric.keyword_difficulty}`);
    }
    if (metric.cpc != null && metric.cpc >= 0) {
      parts.push(`CPC $${metric.cpc.toFixed(2)}`);
    }
  }

  const stats = parts.join(' · '); // " · " separator
  const tip = densityTip(occurrences);

  return `“${display}” — ${stats}. Tip: ${tip}`;
}

export default getKeywordSuggestion;
